import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useGroupLogo } from '../../contexts/ThemeContext';

interface LogoProps {
  pageTitle?: string;
  // map/list toggle (only rendered on Cidades screen)
  displayMode?: 'map' | 'list';
  onDisplayModeChange?: (mode: 'map' | 'list') => void;
  // kept for backward compatibility
  showHeader?: boolean;
  showHeaderButton?: boolean;
  onHeaderButtonClick?: () => void;
}

const HeaderBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  padding: 0 20px;
  z-index: 2000;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const LogoShimmer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 160px;
  height: 42px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const GroupLogo = styled.img<{ $loaded: boolean }>`
  max-width: 240px;
  max-height: 58px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  mix-blend-mode: multiply;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const Separator = styled.div`
  width: 1px;
  height: 22px;
  background: #e2e8f0;
  flex-shrink: 0;
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.2px;
  flex: 1;
`;

const ToggleWrap = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  margin-left: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  ${props => props.$active ? `
    background: white;
    color: var(--accent);
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  ` : `
    background: transparent;
    color: #94a3b8;
    &:hover { color: #64748b; background: rgba(255,255,255,0.6); }
  `}
  svg { width: 16px; height: 16px; }
`;

const Logo: React.FC<LogoProps> = ({ pageTitle, displayMode, onDisplayModeChange }) => {
  const groupLogo = useGroupLogo();
  const [loaded, setLoaded] = useState(false);
  return (
    <HeaderBar>
      {(!groupLogo || !loaded) && <LogoShimmer />}
      {groupLogo && <GroupLogo src={groupLogo} alt="Logo" $loaded={loaded} onLoad={() => setLoaded(true)} />}
      {pageTitle && (
        <>
          <Separator />
          <Title>{pageTitle}</Title>
        </>
      )}
      {displayMode !== undefined && onDisplayModeChange && (
        <ToggleWrap>
          <ToggleBtn $active={displayMode === 'map'} onClick={() => onDisplayModeChange('map')} title="Mapa">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </ToggleBtn>
          <ToggleBtn $active={displayMode === 'list'} onClick={() => onDisplayModeChange('list')} title="Lista">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </ToggleBtn>
        </ToggleWrap>
      )}
    </HeaderBar>
  );
};

export default Logo;
